import axios, { AxiosResponse } from "axios";
import httpApi from "@utils/httpApi";
import { store } from "@app-redux/store";
import { ITrack } from "@src/types/track";
import { ApiResponse } from "@src/types/httpApi";
import { ILibraryFilters } from "@src/redux/slices/librarySlice";
import { FEATURES } from "@src/constants";

export type CreateTrackFile = {
  trackFile: File; // File object representing the audio file
  curatorId?: string;
};

const uploadApiBase =
  process.env.REACT_APP_UPLOAD_API_URL ||
  "https://se8xxd2wz3.eu-west-2.awsapprunner.com";

const runtimeEnv = process.env.REACT_APP_ENV || process.env.NODE_ENV;
const xEnvValue = runtimeEnv === "production" ? "prod" : "dev";

type PresignedUploadResponse = {
  uploadUrl: string;
  publicUrl: string;
  path?: string;
  headers?: Record<string, string>;
};

const getUploadEndpoint = (): string => {
  const trimmedBase = uploadApiBase.replace(/\/+$/, "");
  if (trimmedBase.endsWith("/upload")) {
    return trimmedBase;
  }
  return `${trimmedBase}/upload`;
};

export const getPresignedUploadUrl = async ({
  file,
  curatorId,
}: {
  file: File;
  curatorId?: string;
}): Promise<{
  uploadUrl: string;
  fileUrl?: string;
  headers?: Record<string, string>;
}> => {
  const token = store.getState().auth.token;
  const uploadFolder = curatorId || "";
  const params = new URLSearchParams({
    filename: file.name,
    contentType: file.type || "application/octet-stream",
    uploadFolder,
  });
  const endpoint = `${getUploadEndpoint()}?${params.toString()}`;
  const response = await axios.get<
    { data: PresignedUploadResponse } | PresignedUploadResponse
  >(endpoint, {
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "X-Env": xEnvValue,
    },
  });

  const responseData = response.data;
  const data = "data" in responseData ? responseData.data : responseData;
  const uploadUrl = typeof data === "string" ? data : data.uploadUrl;
  const fileUrl = typeof data === "object" ? data.publicUrl : undefined;

  if (!uploadUrl) {
    throw new Error("Presigned upload URL is missing in response");
  }

  return { uploadUrl, fileUrl, headers: data.headers };
};

export const uploadFileToPresignedUrl = async ({
  file,
  uploadUrl,
  headers,
}: {
  file: File;
  uploadUrl: string;
  headers?: Record<string, string>;
}): Promise<void> => {
  await axios.put(uploadUrl, file, {
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      ...headers,
    },
  });
};

const sanitizeTrackMetadata = (
  metadata?: Omit<Partial<ITrack>, "audio" | "processedAudio">
): Omit<Partial<ITrack>, "audio" | "processedAudio"> => {
  if (!metadata) return {};

  const {
    waveformUrl: _waveformUrl,
    waveformBinaryUrl: _waveformBinaryUrl,
    year,
    ...rest
  } = metadata as Record<string, unknown>;

  const sanitized: Record<string, unknown> = { ...rest };

  if (year !== undefined && year !== null) {
    const normalizedYear = String(year).replace(/\D/g, "");
    if (normalizedYear) {
      sanitized.year = String(normalizedYear);
    }
  }

  return sanitized as Omit<Partial<ITrack>, "audio" | "processedAudio">;
};

/**
 * `waveform` and `waveformBinary` are media relations, so the urls that
 * `tracks/extract` returns cannot be posted under those names — Strapi takes
 * them as `waveformData` / `waveformBinaryData` descriptors instead, the same
 * escape hatch `audioData` uses [ROVR-3829, backend in ROVR-3831]. Stripping
 * them (what we did before) is why tracks created from the dash ended up with
 * `waveform: null`.
 *
 * The urls have to be permanent object urls: the file is stored by reference,
 * so a presigned url would start returning 403 once it expired.
 */
const buildWaveformDescriptors = (
  metadata?: Omit<Partial<ITrack>, "audio" | "processedAudio">
): Record<string, { url: string }> => {
  if (!metadata || !FEATURES.WAVEFORM_BINARY_ENABLED) return {};

  const descriptors: Record<string, { url: string }> = {};
  if (metadata.waveformUrl) {
    descriptors.waveformData = { url: metadata.waveformUrl };
  }
  if (metadata.waveformBinaryUrl) {
    descriptors.waveformBinaryData = { url: metadata.waveformBinaryUrl };
  }
  return descriptors;
};

// Create a new track with a given name
export const createTrack = (
  trackName: string,
  ownerId: string,
  fileId?: number,
  playlistId?: string,
  metadata?: Omit<Partial<ITrack>, "audio" | "processedAudio">,
  audioData?: {
    url: string;
    name: string;
    mime: string;
    size: number;
    ext?: string;
  }
): Promise<AxiosResponse> => {
  if (!fileId && (!audioData || !audioData.url)) {
    throw new Error(
      "fileId or audioData with a valid URL is required to create a track"
    );
  }
  const sanitizedMetadata = sanitizeTrackMetadata(metadata);

  const trackData: {
    title: string;
    audio?: number;
    audioData?: {
      url: string;
      name: string;
      mime: string;
      size: number;
      ext?: string;
    };
    owner?: { set: { documentId: string }[] };
    playlist?: { set: { documentId: string }[] };
    [key: string]: any;
  } = {
    title: trackName,
    ...sanitizedMetadata,
    ...buildWaveformDescriptors(metadata),
  };

  if (fileId) {
    trackData.audio = fileId;
  }
  if (audioData) {
    trackData.audioData = audioData;
  }

  if (playlistId) {
    trackData.playlist = {
      set: [{ documentId: playlistId }],
    };
  }
  if (ownerId) {
    trackData.owner = {
      set: [{ documentId: ownerId }],
    };
  }
  return httpApi.post("/api/tracks", {
    data: trackData,
  });
};

// Upload a track from a URL for an existing track
export const uploadTrackUrl = async (
  trackId: string,
  data: { url: string; name: string; mime: string; size: number; ext?: string }
): Promise<AxiosResponse> => {
  return httpApi.post(`/api/tracks/${trackId}/upload-url`, data);
};

// Add a track to a specific playlist
export const addTrackToPlaylist = (
  trackId: string,
  playlistId: string
): Promise<AxiosResponse> => {
  return httpApi.put(`/api/playlists/${playlistId}`, {
    data: {
      tracks: {
        connect: [{ documentId: trackId }],
      },
    },
  });
};

// Upload a track file associated with a specific track
export const uploadTrackFile = async ({
  trackFile,
  curatorId,
}: CreateTrackFile): Promise<{
  fileUrl: string;
  audioData: {
    url: string;
    name: string;
    mime: string;
    size: number;
    ext?: string;
  };
}> => {
  const { uploadUrl, fileUrl, headers } = await getPresignedUploadUrl({
    file: trackFile,
    curatorId,
  });

  await uploadFileToPresignedUrl({
    file: trackFile,
    uploadUrl,
    headers,
  });

  if (!fileUrl) {
    throw new Error("Presigned upload did not return a file URL");
  }

  const ext = trackFile.name.split(".").pop();
  return {
    fileUrl,
    audioData: {
      url: fileUrl,
      name: trackFile.name,
      mime: trackFile.type || "application/octet-stream",
      size: trackFile.size,
      ext: ext || "",
    },
  };
};

// Upload a replacement cover image for a track and return its public URL, to
// be sent as `coverUrl` on the track update (ROVR-4157). The name is unique
// per upload so it never overwrites another track's cover in the same folder
// or gets served stale from a cache.
export const uploadTrackCover = async ({
  file,
  trackId,
  curatorId,
}: {
  file: File;
  trackId: string;
  curatorId?: string;
}): Promise<string> => {
  const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
  const coverFile = new File([file], `cover-${trackId}-${Date.now()}.${ext}`, {
    type: file.type,
  });
  const { uploadUrl, fileUrl, headers } = await getPresignedUploadUrl({
    file: coverFile,
    curatorId,
  });

  await uploadFileToPresignedUrl({ file: coverFile, uploadUrl, headers });

  if (!fileUrl) {
    throw new Error("Presigned upload did not return a file URL");
  }
  return fileUrl;
};

export const extractAudioMetadata = async (
  trackUrl: string,
  imagesUploadPath: string,
  jwt: string
): Promise<AxiosResponse> => {
  // Extraction now lives behind the AWS Gateway API (ROVR-3828) rather than the
  // standalone lambda, so the path is `/tracks/extract` on the same host that
  // serves the rest of the gateway.
  const extractionApiBase =
    process.env.REACT_APP_EXTRACTION_API_URL ||
    (xEnvValue === "prod"
      ? "https://api.rovr.live"
      : "https://devapi.rovr.live");
  const extractionEndpoint = extractionApiBase.endsWith("/tracks/extract")
    ? extractionApiBase
    : `${extractionApiBase.replace(/\/+$/, "")}/tracks/extract`;
  const response = await fetch(extractionEndpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify({
      track_url: trackUrl,
      images_upload_path: imagesUploadPath,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || "Failed to extract audio metadata");
  }

  return { data, status: response.status } as AxiosResponse;
};

// Fetch tracks with an optional search query
export const fetchApiTracks = async ({
  filters,
  page = 1,
  pageSize = 25,
}: {
  filters?: Partial<ILibraryFilters>;
  page?: number;
  pageSize?: number;
}): Promise<AxiosResponse<ApiResponse<ITrack[]>>> => {
  try {
    const queryParams = new URLSearchParams();

    if (filters?.includedTags?.length) {
      filters.includedTags.forEach((tag, index) => {
        queryParams.append(
          `filters[tags][documentId][$in][${index}]`,
          tag.documentId
        );
      });
    }
    if (filters?.excludedTags?.length) {
      filters.excludedTags.forEach((tag, index) => {
        queryParams.append(
          `filters[tags][documentId][$notIn][${index}]`,
          tag.documentId
        );
      });
    }
    if (filters?.includedMoods?.length) {
      filters.includedMoods.forEach((mood, index) => {
        queryParams.append(
          `filters[moods][documentId][$in][${index}]`,
          mood.documentId
        );
      });
    }
    if (filters?.excludedMoods?.length) {
      filters.excludedMoods.forEach((mood, index) => {
        queryParams.append(
          `filters[moods][documentId][$notIn][${index}]`,
          mood.documentId
        );
      });
    }
    if (filters?.searchText || filters?.title) {
      if (filters.title) {
        queryParams.append("filters[title][$containsi]", filters.title);
      } else if (filters.searchText) {
        const trimmedSearchText = filters.searchText.trim();
        queryParams.append(
          "filters[$or][0][title][$containsi]",
          trimmedSearchText
        );
        queryParams.append(
          "filters[$or][1][artist][$containsi]",
          trimmedSearchText
        );
        queryParams.append(
          "filters[$or][2][album][$containsi]",
          trimmedSearchText
        );
        queryParams.append(
          "filters[$or][3][label][$containsi]",
          trimmedSearchText
        );
        queryParams.append(
          "filters[$or][4][description][$containsi]",
          trimmedSearchText
        );
        queryParams.append(
          "filters[$or][5][bpm][$containsi]",
          trimmedSearchText
        );
        queryParams.append(
          "filters[$or][6][key][$containsi]",
          trimmedSearchText
        );
        queryParams.append(
          "filters[$or][7][isrc][$containsi]",
          trimmedSearchText
        );
        queryParams.append(
          "filters[$or][8][id][$containsi]",
          trimmedSearchText.replace(/\s+/g, "")
        );
        queryParams.append(
          "filters[$or][9][documentId][$containsi]",
          trimmedSearchText.replace(/\s+/g, "")
        );
        queryParams.append(
          "filters[$or][10][bitrate][$containsi]",
          trimmedSearchText
        );
        queryParams.append(
          "filters[$or][11][playlist][documentId][$containsi]",
          trimmedSearchText
        );
      }
    }
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (
        key === "pagination" ||
        key === "title" ||
        key === "searchText" ||
        key === "includedTags" ||
        key === "includedMoods" ||
        key === "excludedTags" ||
        key === "excludedMoods" ||
        key === "isTopLikedThisWeek" ||
        key === "missingYoutube" ||
        key === "missingSpotify" ||
        key === "missingApple" ||
        key === "missingShazam" ||
        key === "createdDateRange" ||
        key === "scheduleDateRange"
      )
        return;
      if (value) {
        if (key === "bpm") {
          return queryParams.append("filters[bpm][$gte]", String(value));
        }
        if (key === "durationInMinutes") {
          return queryParams.append(
            "filters[durationInMinutes][$gte]",
            String(value)
          );
        } else {
          return queryParams.append(
            `filters[${key}][$containsi]`,
            String(value)
          );
        }
      }
    });
    if (page) {
      queryParams.append("pagination[page]", String(page));
    }
    if (pageSize) {
      queryParams.append("pagination[pageSize]", String(pageSize));
    }
    queryParams.append("filters[playlist][playlistStatus][$notIn]", "draft");
    queryParams.append("filters[playlist][playlistStatus][$notIn]", "ready");
    queryParams.append("populate[audio][fields]", "*");
    queryParams.append("populate[tags][fields]", "*");
    queryParams.append("populate[genre][fields]", "*");
    queryParams.append("populate[genre][populate][subGenres][fields]", "*");
    queryParams.append("populate[subGenre][fields]", "*");
    queryParams.append("populate[emotion][fields]", "*");
    queryParams.append("populate[moods][fields]", "*");
    if (filters?.isTopLikedThisWeek) {
      queryParams.append("isTopLikedThisWeek", "true");
    }
    if (filters?.missingYoutube) {
      queryParams.append("missingYoutube", "true");
    }
    if (filters?.missingSpotify) {
      queryParams.append("missingSpotify", "true");
    }
    if (filters?.missingApple) {
      queryParams.append("missingApple", "true");
    }
    if (filters?.missingShazam) {
      queryParams.append("missingShazam", "true");
    }
    const startDate = filters?.createdDateRange?.startDate;
    if (startDate && typeof startDate === "string") {
      queryParams.append("createdDateStart", startDate);
    }

    const endDate = filters?.createdDateRange?.endDate;
    if (endDate && typeof endDate === "string") {
      queryParams.append("createdDateEnd", endDate);
    }

    const scheduleStartDate = filters?.scheduleDateRange?.startDate;
    if (scheduleStartDate && typeof scheduleStartDate === "string") {
      queryParams.append("scheduleDateStart", scheduleStartDate);
    }

    const scheduleEndDate = filters?.scheduleDateRange?.endDate;
    if (scheduleEndDate && typeof scheduleEndDate === "string") {
      queryParams.append("scheduleDateEnd", scheduleEndDate);
    }
    return httpApi.get(`/api/tracks/library?${queryParams.toString()}`);
  } catch (error) {
    return Promise.reject(error);
  }
};

// Update a track with new data
export const updateTrack = async (
  trackId: string,
  trackData: Partial<ITrack>
): Promise<AxiosResponse> => {
  return httpApi.put(`/api/tracks/${trackId}`, {
    data: trackData,
  });
};

export const fetchUntaggedTracks = async ({
  page = 1,
  pageSize = 25,
  searchText,
  genreIds,
  emotionIds,
  tagIds,
}: {
  page?: number;
  pageSize?: number;
  searchText?: string;
  genreIds?: string[];
  emotionIds?: string[];
  tagIds?: string[];
}): Promise<AxiosResponse<ApiResponse<ITrack[]>>> => {
  try {
    const queryParams = new URLSearchParams();
    if (page) {
      queryParams.append("pagination[page]", String(page));
    }
    if (pageSize) {
      queryParams.append("pagination[pageSize]", String(pageSize));
    }
    if (searchText) {
      queryParams.append("filters[title][$containsi]", searchText);
    }
    // Add genre filters (genre is a single object relation, not an array)
    if (genreIds && genreIds.length > 0) {
      if (genreIds.length === 1) {
        queryParams.append("filters[genre][documentId][$eq]", genreIds[0]);
      } else {
        genreIds.forEach((id, index) => {
          queryParams.append(`filters[genre][documentId][$in][${index}]`, id);
        });
      }
    }
    // Add emotion filters (emotion is a single object relation, not an array)
    if (emotionIds && emotionIds.length > 0) {
      if (emotionIds.length === 1) {
        queryParams.append("filters[emotion][documentId][$eq]", emotionIds[0]);
      } else {
        emotionIds.forEach((id, index) => {
          queryParams.append(`filters[emotion][documentId][$in][${index}]`, id);
        });
      }
    }
    // Add tags filters
    if (tagIds && tagIds.length > 0) {
      tagIds.forEach((id, index) => {
        queryParams.append(`filters[tags][documentId][$in][${index}]`, id);
      });
    }
    queryParams.append("populate[audio][fields]", "*");
    queryParams.append("populate[tags][fields]", "*");
    queryParams.append("populate[genre][fields]", "*");
    queryParams.append("populate[genre][populate][subGenres][fields]", "*");
    queryParams.append("populate[subGenre][fields]", "*");
    queryParams.append("populate[emotion][fields]", "*");
    queryParams.append("populate[moods][fields]", "*");
    return httpApi.get(`/api/tracks?${queryParams.toString()}`);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const fetchTrackShazamInfo = async (
  trackId: string
): Promise<ITrack> => {
  // Build fields in Strapi's indexed form (fields[0]=…), matching the rest of
  // the codebase. axios's default array serialization (fields[]=…) isn't
  // recognized by Strapi, so the selection would silently be ignored.
  const queryParams = new URLSearchParams();
  ["documentId", "shazamInfo", "shazamAttempts"].forEach((field, index) => {
    queryParams.append(`fields[${index}]`, field);
  });
  const response = await httpApi.get(
    `/api/tracks/${trackId}?${queryParams.toString()}`
  );
  return (response?.data?.data ?? response?.data) as ITrack;
};

export const fetchTrackWithAudio = async (
  trackId: string
): Promise<AxiosResponse<ITrack>> => {
  return httpApi.get(`/api/tracks/${trackId}`, {
    params: {
      populate: {
        audio: {
          fields: ["*"],
        },
        tags: {
          fields: ["*"],
        },
        genre: {
          fields: ["*"],
          populate: { subGenres: { fields: ["*"] } },
        },
        subGenre: {
          fields: ["*"],
        },
        emotion: {
          fields: ["*"],
        },
        moods: {
          fields: ["*"],
        },
      },
      fields: ["documentId"],
    },
  });
};

export const fetchTrackDownloadUrlApi = async (
  trackId: string
): Promise<AxiosResponse<{ url: string; expiresIn: number }>> => {
  return httpApi.post(`/api/tracks/${trackId}/download-url`);
};
